import Button from '@/app/components/Button';
import SectionContainer from '@/app/components/SectionContainer';
import DrDaveSurfing from '@/app/modules/HomePageV2/assets/DrDaveSurfing.jpg';
import DrDaveSurfsUp from '@/app/modules/HomePageV2/assets/DrDaveSurfsUp.jpg';
import DrDaveWork from '@/app/modules/HomePageV2/assets/DrDaveWork.jpg';
import { useCarousel } from '@/app/modules/HomePageV2/hooks/useCarousel';
import { useVisibilityObserver } from '@/app/modules/HomePageV2/hooks/useVisibilityObserver';
import { appClientConfig } from '@/app/utils/appClientConfig';
import { useAnalytics } from '@/app/utils/useAnalytics';
import clsx from 'clsx';
import Image, { StaticImageData } from 'next/image';

import styles from './LifestyleSection.module.scss';

interface LifestyleSectionProps {
  headingLevel: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  doctorName: string;
}

export const LifestyleSection = ({
  headingLevel: HeadingComponent,
  doctorName,
}: LifestyleSectionProps) => {
  const track = useAnalytics();
  const profileUrl = appClientConfig.practitionerProfileUrl ?? '';
  const { elementRef, isVisible } = useVisibilityObserver({ threshold: 0.2 });

  // we can extract this into a prop later
  const carouselImages: StaticImageData[] = [DrDaveWork, DrDaveSurfing, DrDaveSurfsUp];

  const { currentIndex } = useCarousel({
    carouselData: carouselImages,
    isVisible: isVisible,
    transitionDuration: 500,
    autoAdvanceInterval: 3000,
  });

  return (
    <SectionContainer ref={elementRef} className={styles.lifestyleSection}>
      <div className={styles.imageContainer}>
        {carouselImages.map((image, index) => (
          <Image
            key={image.src}
            src={image.src}
            alt=""
            aria-hidden="true"
            priority={index === 0}
            placeholder="blur"
            blurDataURL={image.blurDataURL}
            className={clsx(styles.lifestyleImage, index === currentIndex && styles.visible)}
            fill
            sizes="(max-width: 320px) 320px, (max-width: 599px) 100vw, (max-width: 959px) 50vw, 50vw"
            style={{
              objectFit: 'cover',
            }}
          />
        ))}
        <div className={styles.drNamePill}>{doctorName}</div>
      </div>
      <div className={styles.contentContainer}>
        <HeadingComponent className={styles.title}>
          <span>Helping doctors</span> live the lives they want
        </HeadingComponent>
        <p className={styles.description}>
          Whether you’re after lifestyle flexibility, higher pay, or career progression, Medrecruit
          is here to help. As Australasia's largest medical recruitment agency, we simplify your
          career management, offering a free, end-to-end service for doctors.
        </p>
        <Button
          className={styles.joinUsButton}
          href={`${profileUrl}/sign-up`}
          onClick={() =>
            track('Click', {
              action: `Lifestyle section - Register today`,
              'element text': 'Register today',
              'element type': 'button',
            })
          }
          trackFunction={eventProps => {
            track('Click', {
              ...eventProps,
              action: 'Lifestyle section - Register today',
            });
          }}
        >
          Register today
        </Button>
      </div>
    </SectionContainer>
  );
};
